import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, X } from 'lucide-react'

export function stripDiacritics(str: string): string {
  if (!str) return ''
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
}

export function defaultTypeaheadFilter<T>(
  item: T,
  query: string,
  displayValue: (item: T) => string,
): boolean {
  if (!query) return true
  const cleanQ = stripDiacritics(query).trim()
  if (!cleanQ) return true

  // Check displayValue
  const disp = stripDiacritics(displayValue(item))
  if (disp.includes(cleanQ)) return true

  // Check common properties: code, name, title if present
  if (typeof item === 'object' && item !== null) {
    const obj = item as Record<string, any>
    if (obj.code && stripDiacritics(String(obj.code)).includes(cleanQ)) return true
    if (obj.name && stripDiacritics(String(obj.name)).includes(cleanQ)) return true
    if (obj.title && stripDiacritics(String(obj.title)).includes(cleanQ)) return true
    if (obj.item_code && stripDiacritics(String(obj.item_code)).includes(cleanQ)) return true
    if (obj.variant_code && stripDiacritics(String(obj.variant_code)).includes(cleanQ)) return true
  }

  return false
}

export interface DropdownPosition {
  top?: number
  bottom?: number
  left: number
  minWidth: number
  maxWidth: number
  maxHeight: number
  flipUp: boolean
}

export function calculateDropdownPosition(params: {
  inputRect: { top: number; bottom: number; left: number; right: number; width: number; height: number }
  windowWidth: number
  windowHeight: number
  dropdownWidth?: number
  dropdownHeight?: number
}): DropdownPosition | null {
  const { inputRect, windowWidth, windowHeight } = params

  // Ô input bị cuộn ra khỏi viewport -> đóng hộp
  if (
    inputRect.bottom < 0 ||
    inputRect.top > windowHeight ||
    inputRect.right < 0 ||
    inputRect.left > windowWidth
  ) {
    return null
  }

  const height = params.dropdownHeight ?? 220
  const width = params.dropdownWidth ?? Math.max(inputRect.width, 220)

  const spaceBelow = windowHeight - inputRect.bottom
  const spaceAbove = inputRect.top

  // 3. Tự lật lên trên (flip up) nếu khoảng trống phía dưới màn hình không đủ:
  // Nếu inputRect.bottom + dropdownHeight > windowHeight && inputRect.top > dropdownHeight -> mở ngược lên trên ô input
  const flipUp = (inputRect.bottom + height > windowHeight || spaceBelow < 160) && spaceAbove > height

  // 4. Tự dịch sang trái nếu chạm viền phải màn hình (không để bị khuất mép phải)
  let left = inputRect.left
  const maxWidth = Math.max(inputRect.width, 360)
  if (left + width > windowWidth - 8) {
    left = Math.max(8, windowWidth - width - 8)
  }

  return {
    top: flipUp ? undefined : Math.round(inputRect.bottom + 4),
    bottom: flipUp ? Math.round(windowHeight - inputRect.top + 4) : undefined,
    left: Math.round(left),
    minWidth: Math.round(inputRect.width),
    maxWidth,
    maxHeight: flipUp
      ? Math.min(240, Math.max(100, spaceAbove - 16))
      : Math.min(240, Math.max(100, spaceBelow - 16)),
    flipUp,
  }
}

export interface TypeaheadProps<T> {
  id?: string
  name?: string
  items: T[]
  value?: T | null
  onChange: (item: T | null) => void
  displayValue: (item: T) => string
  getItemKey?: (item: T, idx: number) => string | number
  filterFn?: (item: T, query: string) => boolean
  renderItem?: (item: T, isSelected: boolean, isHighlighted: boolean) => React.ReactNode
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  autoFocus?: boolean
  clearable?: boolean
  className?: string
  inputClassName?: string
  dropdownClassName?: string
  error?: boolean | string
  'aria-label'?: string
  inputValue?: string
  onInputChange?: (value: string) => void
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>
  allowCustomValue?: boolean
  showDropdownButton?: boolean
}

export function Typeahead<T>({
  id,
  name,
  items,
  value,
  onChange,
  displayValue,
  getItemKey,
  filterFn,
  renderItem,
  placeholder = 'Tìm hoặc chọn...',
  disabled = false,
  readOnly = false,
  autoFocus = false,
  clearable = true,
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  error,
  'aria-label': ariaLabel,
  inputValue,
  onInputChange,
  onFocus,
  onPaste,
  allowCustomValue = false,
  showDropdownButton = true,
}: TypeaheadProps<T>): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState(() => (inputValue !== undefined ? inputValue : (value ? displayValue(value) : '')))
  const [highlightIndex, setHighlightIndex] = useState(0)
  const [coords, setCoords] = useState<DropdownPosition>({
    left: 0,
    minWidth: 120,
    maxWidth: 360,
    maxHeight: 240,
    flipUp: false,
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Synchronize text when value or inputValue changes
  useEffect(() => {
    if (inputValue !== undefined) {
      setQuery(inputValue)
    } else if (value) {
      setQuery(displayValue(value))
    } else {
      setQuery('')
    }
  }, [inputValue, value, displayValue])

  // Filter items based on query
  const filteredItems = useMemo(() => {
    if (!isOpen) return items
    const customFilter = filterFn || ((item: T, q: string) => defaultTypeaheadFilter(item, q, displayValue))
    return items.filter((item) => customFilter(item, query))
  }, [items, isOpen, filterFn, query, displayValue])

  // Reset highlight index when filtered list changes
  useEffect(() => {
    setHighlightIndex(0)
  }, [filteredItems])

  // Update floating dropdown coordinates
  const updateCoords = useCallback(() => {
    if (!containerRef.current || typeof window === 'undefined') return
    const rect = containerRef.current.getBoundingClientRect()
    const dropdownEl = dropdownRef.current

    const pos = calculateDropdownPosition({
      inputRect: rect,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      dropdownWidth: dropdownEl?.offsetWidth,
      dropdownHeight: dropdownEl?.offsetHeight,
    })

    if (!pos) {
      setIsOpen(false)
      return
    }

    setCoords((prev) => {
      if (
        prev.top === pos.top &&
        prev.bottom === pos.bottom &&
        prev.left === pos.left &&
        prev.minWidth === pos.minWidth &&
        prev.maxWidth === pos.maxWidth &&
        prev.maxHeight === pos.maxHeight &&
        prev.flipUp === pos.flipUp
      ) {
        return prev
      }
      return pos
    })
  }, [])

  // Recalculate on open or items change
  useEffect(() => {
    if (isOpen) {
      updateCoords()
    }
  }, [isOpen, filteredItems, updateCoords])

  // Listen to window scroll (capture) and resize
  useEffect(() => {
    if (!isOpen) return

    let rafId: number | null = null
    const handleScrollOrResize = () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        updateCoords()
      })
    }

    // capture: true ensures capturing scroll events in nested scroll containers (like table grid)
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [isOpen, updateCoords])

  // Click outside to close (checks both container and portal dropdown)
  useEffect(() => {
    if (!isOpen) return

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      const insideContainer = containerRef.current && containerRef.current.contains(target)
      const insideDropdown = dropdownRef.current && dropdownRef.current.contains(target)

      if (!insideContainer && !insideDropdown) {
        setIsOpen(false)
        if (allowCustomValue) return
        // Reset query to current value display
        if (value) {
          setQuery(displayValue(value))
        } else {
          setQuery('')
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, value, displayValue, allowCustomValue])

  const handleSelect = (item: T) => {
    onChange(item)
    const text = displayValue(item)
    setQuery(text)
    onInputChange?.(text)
    setIsOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    setQuery('')
    onInputChange?.('')
    setIsOpen(false)
    inputRef.current?.focus()
  }

  const handleBlur = (e: React.FocusEvent) => {
    const nextTarget = e.relatedTarget as Node | null
    if (
      (containerRef.current && containerRef.current.contains(nextTarget)) ||
      (dropdownRef.current && dropdownRef.current.contains(nextTarget))
    ) {
      return
    }
    setIsOpen(false)
    if (allowCustomValue) return
    if (value) {
      setQuery(displayValue(value))
    } else {
      setQuery('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled || readOnly) return

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        e.preventDefault()
        updateCoords()
        setIsOpen(true)
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((prev) => (prev < filteredItems.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : filteredItems.length - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filteredItems.length > 0 && highlightIndex >= 0 && highlightIndex < filteredItems.length) {
        handleSelect(filteredItems[highlightIndex])
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setIsOpen(false)
      if (allowCustomValue) return
      if (value) setQuery(displayValue(value))
      else setQuery('')
    }
  }

  const hasError = Boolean(error)
  const baseContainerClass =
    'relative inline-flex items-center rounded border bg-white transition-colors ' +
    (hasError
      ? 'border-red-500 ring-1 ring-red-500'
      : 'border-slate-300 focus-within:border-emerald-600 focus-within:ring-1 focus-within:ring-emerald-600') +
    (disabled ? ' bg-slate-50 opacity-60 cursor-not-allowed' : '')

  const isClient = typeof document !== 'undefined'

  const dropdownMenu = (
    <div
      ref={dropdownRef}
      data-testid="typeahead-portal-dropdown"
      onMouseDown={(e) => {
        // Prevent input from losing focus when clicking inside dropdown or scrollbar
        e.preventDefault()
      }}
      style={{
        position: 'fixed',
        top: coords.top !== undefined ? `${coords.top}px` : 'auto',
        bottom: coords.bottom !== undefined ? `${coords.bottom}px` : 'auto',
        left: `${coords.left}px`,
        minWidth: `${coords.minWidth}px`,
        maxWidth: `${coords.maxWidth}px`,
        maxHeight: `${coords.maxHeight}px`,
        zIndex: 9999,
      }}
      className={`overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-xl ${dropdownClassName}`.trim()}
    >
      {filteredItems.length === 0 ? (
        <div className="px-3 py-2 text-xs text-slate-400 italic">
          Không tìm thấy kết quả
        </div>
      ) : (
        filteredItems.map((item, idx) => {
          const isSelected = value !== null && value !== undefined && displayValue(item) === displayValue(value)
          const isHighlighted = idx === highlightIndex
          const key = getItemKey ? getItemKey(item, idx) : idx

          return (
            <div
              key={key}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setHighlightIndex(idx)}
              className={`cursor-pointer px-3 py-1.5 text-xs transition-colors break-words whitespace-normal ${
                isHighlighted
                  ? 'bg-emerald-50 text-emerald-900 font-medium'
                  : isSelected
                    ? 'bg-slate-50 font-semibold text-slate-900'
                    : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              {renderItem ? renderItem(item, isSelected, isHighlighted) : displayValue(item)}
            </div>
          )
        })
      )}
    </div>
  )

  return (
    <div ref={containerRef} className={`${baseContainerClass} ${className}`.trim()}>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        onChange={(e) => {
          const val = e.target.value
          setQuery(val)
          onInputChange?.(val)
          if (!isOpen) {
            updateCoords()
            setIsOpen(true)
          }
        }}
        onFocus={(event) => {
          if (!disabled && !readOnly) {
            updateCoords()
            setIsOpen(true)
          }
          onFocus?.(event)
        }}
        onBlur={handleBlur}
        onPaste={onPaste}
        onKeyDown={handleKeyDown}
        className={`w-full min-w-0 bg-transparent px-2.5 py-1 text-xs sm:text-sm font-sans text-slate-800 outline-none placeholder:text-slate-400 ${inputClassName}`.trim()}
        style={{ fieldSizing: 'content' as any }}
      />

      <div className="flex items-center pr-2 gap-0.5 shrink-0">
        {clearable && !disabled && !readOnly && query && (
          <button
            type="button"
            tabIndex={-1}
            onClick={handleClear}
            className="text-slate-400 hover:text-slate-600 p-0.5"
            aria-label="Xóa chọn"
          >
            <X className="h-3 w-3" />
          </button>
        )}
        {showDropdownButton && (
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled || readOnly}
            onClick={() => {
              if (!disabled && !readOnly) {
                if (!isOpen) updateCoords()
                setIsOpen((prev) => !prev)
                inputRef.current?.focus()
              }
            }}
            className="text-slate-400 hover:text-slate-600 p-0.5"
          >
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* Dropdown Menu via React Portal to document.body */}
      {isOpen && !disabled && !readOnly && isClient && createPortal(dropdownMenu, document.body)}
    </div>
  )
}
